import { computed, unref } from 'vue'
import { useRoute } from 'vue-router'
import { useAsyncData } from 'nuxt/app'
import builderIoService from '~/composables/builderIoService'
import { useLocaleStore } from '~/stores/localeStore'
import { CATEGORY_DETAILS, CONTENT_TYPES } from '~~/shared/utils/contentTypes'
import type { FaqEntryData, BuilderEntryWithId } from '~~/shared/types/common'
import { normalizePath } from '~~/shared/utils/helper'

const escapeRegex = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const allowByWhitelist = (countryCode: string) => {
  return {
    $or: [
      { 'data.whitelistCountryRestrictions': { $size: 0 } },
      { 'data.whitelistCountryRestrictions': { $exists: false } },
      { 'data.whitelistCountryRestrictions.countryCode': { $in: [countryCode] } }
    ],
  };
}

const blockByBlacklist = (countryCode: string) => {
  return {
    $and: [
      { 'data.blacklistCountryRestrictions.countryCode': { $nin: [countryCode] } },
      { 'data.countryRestrictionsBlacklist.countryCode': { $nin: [countryCode] } },
    ],
  };
}

const useLocaleCountryCode = () => {
  const localeStore = useLocaleStore();
  return computed(() => {
    const code = (localeStore.locale as any)?.country?.code;
    return typeof code === 'string' && code.trim() ? code : 'US';
  });
};

export const useFaqLanding = () => {
  const service = builderIoService();

  const limit = 100;
  const localeCountryCode = useLocaleCountryCode();

  const { data, error, pending, refresh } = useAsyncData(
    `faqs-${Date.now()}`,
    async () => {
      const countryCode = localeCountryCode.value;
      const query = {
        $and: [
          allowByWhitelist(countryCode),
          blockByBlacklist(countryCode)
        ],
      };

      const allFaqs: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const fetchOptions: any = {
          query,
          limit,
          offset,
          enrich: true,
        };

        const result = await service.fetchManyModels(
          CONTENT_TYPES.FAQS,
          '/',
          false,
          fetchOptions
        );

        const items = result || [];
        const itemsCount = items.length;

        if (itemsCount > 0) {
          allFaqs.push(...items);
        }

        if (itemsCount < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }
      return allFaqs;
    },
    {
      server: true,
      lazy: false,
      default: () => [],
      watch: [localeCountryCode]
    }
  );

  return { data, error, pending, refresh };
};

export const useFaqDetail = (faqUrlKey: Ref<string> | string) => {
  const service = builderIoService();
  const localeCountryCode = useLocaleCountryCode();

  const sanitizedKey = computed(() => {
    const raw = typeof faqUrlKey === 'string' ? faqUrlKey : faqUrlKey.value;
    const stripped = raw.replaceAll(/(^")|("$)/g, '');

    try {
      return decodeURIComponent(stripped);
    } catch {
      return stripped;
    }
  });

  const { data, error, pending, refresh } = useAsyncData<{
    faqEntry: BuilderEntryWithId<FaqEntryData> | null;
    sidebarResult: BuilderEntryWithId<FaqEntryData>[];
  }>(
    () => `faqs-${sanitizedKey.value}-${localeCountryCode.value}`,
    async () => {
      const countryCode = localeCountryCode.value;

      const query = {
        $and: [
          allowByWhitelist(countryCode),
          blockByBlacklist(countryCode),
          { 'data.faqUrlKey': sanitizedKey.value },
        ],
      };

      const fetchOptions: any = {
        query,
        cachebust: true,
      };
      const faqResult = await service.fetchManyModels(
        CONTENT_TYPES.FAQS,
        '/',
        false,
        fetchOptions
      );

      const faqEntry = (faqResult?.[0] as BuilderEntryWithId<FaqEntryData>) ?? null;
      let sidebarResult: BuilderEntryWithId<FaqEntryData>[] = [];

      if (faqEntry?.id) {
        const otherHelpfulQuery = {
          $and: [
            allowByWhitelist(countryCode),
            blockByBlacklist(countryCode),
            { id: { $ne: faqEntry.id } },
            { 'data.faqCategory': faqEntry.data.faqCategory },
          ],
        };

        const otherHelpfulOptions: any = {
          query: otherHelpfulQuery,
          sort: {
            'data.faqUpvotes': -1,
          },
          limit: 3,
          enrich: true,
        };

        sidebarResult = (await service.fetchManyModels(
          CONTENT_TYPES.FAQS,
          '/',
          false,
          otherHelpfulOptions
        )) as BuilderEntryWithId<FaqEntryData>[];
      }

      return { faqEntry, sidebarResult };
    },
    {
      server: true,
      lazy: false,
      default: () => ({ faqEntry: null, sidebarResult: [] }),
      watch: [localeCountryCode, sanitizedKey],
    }
  );

  const faq = computed(() => data.value?.faqEntry ?? null);
  const faqSidebarData = computed(() => data.value?.sidebarResult ?? []);

  return { faq, faqSidebarData, error, pending, refresh };
};

export const useFaqList = () => {
  const service = builderIoService();
  const limit = 500;
  const route = useRoute();
  const localeCountryCode = useLocaleCountryCode();

  // Current normalized path (e.g., '/faqs/shipping')
  const currentPath = computed(() => normalizePath(route.path));

  // Derive Builder category key from the URL map; fallback if unknown
  const category = computed(() => {
    const match = CATEGORY_DETAILS.find(
      (c) => normalizePath(c.url) === currentPath.value
    );
    return match?.builderIoKey ?? 'SHIPPING';
  });

  // Raw query from /faqs/search?q=...
  const qRaw = computed(() => String(route.query.q ?? '').trim());

  const { data, error, pending, refresh } = useAsyncData(
    () => `faqs-${localeCountryCode.value}-${category.value}-${qRaw.value || 'noq'}`,
    async () => {
      const countryCode = localeCountryCode.value;

      // Decode + split into tokens (single or multiple)
      let decoded = qRaw.value;
      try {
        decoded = decodeURIComponent(decoded);
      } catch {
        /* keep raw if malformed */
      }
      decoded = decoded.replace(/\+/g, ' ').trim();

      const tokens = decoded
        ? decoded.split(/\s+/).map((t) => t.trim()).filter(Boolean)
        : [];

      // Build $or across tokens & fields (ANY token logic)
      const searchOrClauses: any[] = [];
      if (tokens.length) {
        for (const faqSubstring of tokens) {
          const esc = escapeRegex(faqSubstring);
          const pattern = `.*${esc}.*`; // substring match anywhere (case-insensitive)
          searchOrClauses.push({ 'data.faqQuestion': { $regex: pattern, $options: 'i' } });
          searchOrClauses.push({ 'data.faqAnswer': { $regex: pattern, $options: 'i' } });
        }
      }

      const query: any = {
        $and: [
          allowByWhitelist(countryCode),
          blockByBlacklist(countryCode),
          ...(searchOrClauses.length
            ? [{ $or: searchOrClauses }] // searching → ignore category filter
            : [{ 'data.faqCategory': unref(category) }]
          ),
        ],
      };

      const fetchOptions: any = {
        query,
        limit,
        cachebust: true,
        sort: {
          'data.faqUpdatedAt': -1
        }
      };

      return service.fetchManyModels(CONTENT_TYPES.FAQS, '/', false, fetchOptions);
    },
    {
      server: true,
      lazy: false,
      default: () => [],
      watch: [category, localeCountryCode, qRaw],
    }
  );

  return { data, error, pending, refresh };
};
